import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

// Small pieces shared by the Withdrawals, KYC and Refunds pages. Styles:
// ./withdrawals.css (wd-*) on top of ../Revenue/revenue.css (rv-*).

/// Keeps only the params that carry a value.
export const clean = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/// Unix seconds (the payout APIs) or an ISO string → dayjs, else null.
const toDay = (t) => {
  if (t === null || t === undefined || t === "") return null;
  const n = Number(t);
  const d = Number.isFinite(n) && String(t).trim() !== "" ? dayjs.unix(n) : dayjs(t);
  return d.isValid() ? d : null;
};

export const fmtTime = (t) => {
  const d = toDay(t);
  return d ? d.format("MMM D, YYYY · h:mm A") : "—";
};

/// "5 min ago" / "3 h ago" / "2 d ago".
export const fmtAgo = (t) => {
  const d = toDay(t);
  if (!d) return "";
  const min = Math.max(0, Math.round((Date.now() - d.valueOf()) / 60000));
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};

export const num = (n, digits = 2) =>
  n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: digits });

export const usd = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

/// "held_clawed" → "Held clawed".
export const humanize = (s) => {
  const t = String(s || "").replace(/[_-]+/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "—";
};

/// The endpoint is not on the server (yet): a plain route 404, not a
/// "row not found" (those carry a `code`).
export const isNotDeployed = (e) => e?.response?.status === 404 && !e?.response?.data?.code;

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to the textarea fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

export function CopyButton({ value, label = "Copy" }) {
  const [state, setState] = useState("");
  useEffect(() => {
    if (!state) return undefined;
    const t = setTimeout(() => setState(""), 1500);
    return () => clearTimeout(t);
  }, [state]);
  if (value === null || value === undefined || value === "") return null;
  return (
    <button
      type="button"
      className={`wd-copy ${state}`}
      title={`Copy ${String(value)}`}
      onClick={async (e) => {
        e.stopPropagation();
        setState((await copyText(String(value))) ? "ok" : "fail");
      }}
    >
      {state === "ok" ? "Copied" : state === "fail" ? "Failed" : label}
    </button>
  );
}

/// Debounced copy of [value].
export function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/// Success line that clears itself.
export function useNotice(ms = 4000) {
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), ms);
    return () => clearTimeout(t);
  }, [notice, ms]);
  return [notice, setNotice];
}

/// Confirm dialog. [children] = the explanation / inputs.
export function ConfirmModal({ title, children, confirmText, tone = "", busy = false, disabled = false, error = "", onConfirm, onClose }) {
  return (
    <div className="rv-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="rv-modal wd-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h4 className="rv-panel-title mb-2">{title}</h4>
        {children}
        {error && <div className="rv-error mt-3">{error}</div>}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="rv-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={`rv-btn ${tone}`} onClick={onConfirm} disabled={busy || disabled}>
            {busy ? "Working…" : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotDeployed({ endpoint }) {
  return (
    <div className="wd-not-deployed">
      <b>Not deployed yet.</b> The server does not have <code>{endpoint}</code> yet — this tab works as soon as the
      backend release that adds it is live.
    </div>
  );
}
